import axios from "axios";

export const API_URL =
  process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const API = axios.create({ baseURL: API_URL });

// Resolve a menu image URL: relative paths (/static/...) get the API host
// prefixed; absolute URLs (http(s)://...) and data: URLs pass through.
export const resolveImageUrl = (url) => {
  if (!url) return null;
  if (/^(https?:|data:)/i.test(url)) return url;
  return `${API_URL.replace(/\/$/, "")}${url.startsWith("/") ? "" : "/"}${url}`;
};

// Weak-network optimisation: when the image is a Cloudinary delivery URL,
// request a small, auto-format (WebP/AVIF), auto-quality, width-capped
// version instead of the full upload. Local/static URLs pass through.
export const cloudImg = (url, width = 400) => {
  const u = resolveImageUrl(url);
  if (!u) return null;
  if (/res\.cloudinary\.com\/.+\/upload\//.test(u)) {
    return u.replace(/\/upload\//, `/upload/f_auto,q_auto,c_limit,w_${width}/`);
  }
  return u;
};

// Public menu for a restaurant (no auth required)
export const fetchPublicMenu = async (restaurantId) => {
  const res = await API.get(`/public/menu/${restaurantId}`);
  return res.data;
};

// Recommendations strip for a restaurant (no auth). Returns
//   { chef_picks: [...], top_sellers: [...] }
// each capped at 6 items. The customer app renders them as two distinct
// labeled strips ("Recommended by Chef" + "Loved by our customers");
// either array may be empty and that strip then hides itself.
export const fetchRecommendations = async (restaurantId) => {
  const res = await API.get(`/public/recommendations/${restaurantId}`);
  return res.data;
};

// Item-to-item co-occurrence ("often ordered with"). Given the menu_ids
// currently in the guest's cart, returns up to 4 items frequently
// ordered alongside them in the last 90 days. Excludes items already
// in the cart and unavailable items. Returns [] when there's no
// co-order history — the cart drawer hides the strip in that case.
export const fetchOftenOrderedWith = async (restaurantId, cartItemIds) => {
  const ids = (cartItemIds || [])
    .map((x) => parseInt(x, 10))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (ids.length === 0) return [];
  const res = await API.get(
    `/public/often-ordered-with/${restaurantId}`,
    { params: { items: ids.join(",") } },
  );
  return res.data;
};

// Legacy lookup: by internal table primary key. Used by old QR codes
// printed before the by-number endpoint existed.
export const fetchTableInfo = async (tableId) => {
  const res = await API.get(`/public/table/${tableId}`);
  return res.data;
};

// Friendlier lookup: by (restaurant_id, table_number). Stable across
// re-seeds and recreations because it doesn't depend on internal PKs.
export const fetchTableByNumber = async (restaurantId, tableNumber) => {
  const res = await API.get(
    `/public/table/by-number/${restaurantId}/${encodeURIComponent(tableNumber)}`
  );
  return res.data;
};

// Branding for a restaurant (no auth required). Used by pages that don't
// have a table context (receipt, rating). Always returns the keys -- URLs
// are null when the restaurant hasn't uploaded branding.
export const fetchPublicBranding = async (restaurantId) => {
  const res = await API.get(`/public/branding/${restaurantId}`);
  return res.data;
};

// Public info for an order: restaurant_id + branding URLs. No auth.
// Used by the rating page so the customer sees the hotel's brand even
// when navigating directly to /rate/:orderId. Keyed on the order's
// unguessable public_token (never the sequential id).
export const fetchPublicOrderInfo = async (token) => {
  const res = await API.get(`/public/order-info/${token}`);
  return res.data;
};

// Build the public receipt URL for an order from its public_token. Opened
// in a new tab / used as a print link on the confirmation page.
export const receiptUrl = (token) =>
  `${API_URL.replace(/\/$/, "")}/public/receipt/${token}`;

// Place an order. We forward per-item notes and modifiers so the kitchen
// sees the customer's selections (no onion, extra cheese, etc.) and the
// order total reflects modifier surcharges. `clientOrderId` is the
// idempotency key — the backend dedupes on it, so retries are safe.
export const placeOrder = async ({ tableId, items, notes, clientOrderId }) => {
  const res = await API.post("/order/", {
    table_id: tableId,
    notes,
    client_order_id: clientOrderId || undefined,
    items: items.map((i) => {
      const out = { menu_id: i.menu_id, quantity: i.quantity };
      if (i.notes) out.notes = i.notes;
      if (i.modifiers && i.modifiers.length) out.modifiers = i.modifiers;
      return out;
    }),
  });
  return res.data;
};

// True for failures worth retrying: network blips (no response at all)
// and gateway/unavailable errors. 4xx responses are real rejections —
// retrying those would just repeat the same "no" four times.
const isTransient = (err) => {
  if (!err.response) return true; // network error / timeout
  return [502, 503, 504].includes(err.response.status);
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Place an order with automatic retry on network blips. Backoff:
// 1s, 2s, 4s, 8s (5 attempts total ≈ 15s of patience — covers the
// typical hotel-WiFi hiccup without stranding the guest forever).
// The same clientOrderId is sent every attempt, so even if an earlier
// attempt actually reached the server, the order is created only once.
// onRetry(attempt, maxAttempts) lets the UI show "reconnecting…".
export const placeOrderWithRetry = async (payload, onRetry) => {
  const MAX_ATTEMPTS = 5;
  let lastErr;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      return await placeOrder(payload);
    } catch (err) {
      lastErr = err;
      if (!isTransient(err) || attempt === MAX_ATTEMPTS) throw err;
      if (onRetry) onRetry(attempt, MAX_ATTEMPTS);
      await sleep(1000 * 2 ** (attempt - 1));
    }
  }
  throw lastErr;
};

export default API;

// Lookup an order's current state -- used by OrderConfirmationPage to
// know when the kitchen has flipped status to "served" so we can prompt
// the guest to leave a rating.
export const fetchOrder = async (orderId) => {
  const res = await API.get(`/order/${orderId}`);
  return res.data;
};

// Has this order already been rated? Returns the rating object or null.
// Keyed on the order's unguessable public_token.
export const fetchExistingRating = async (token) => {
  const res = await API.get(`/ratings/${token}`);
  return res.data;
};

// Submit a rating after the order has been served.
export const submitVisitRating = async (payload) => {
  const res = await API.post(`/ratings/visit`, payload);
  return res.data;
};

export const submitRating = async (token, payload) => {
  // payload: { food_rating, service_rating, food_comment?, service_comment? }
  // token is the order's unguessable public_token.
  const res = await API.post(`/ratings/${token}`, payload);
  return res.data;
};
